import { Box, Chip, ExternalLink, Text, Tooltip } from '@color/continuum';
import { useLayoutEffect, useRef, useState, type MouseEvent, type RefObject } from 'react';
import { generatePath } from 'react-router-dom';

import { VARIANT_CLASSIFICATION_DETAIL_PATH } from '../../VariantClassification/paths';
import { CopyableText } from '../CopyableText';
import { availablePrimerActions } from '../DetailView/PrimersSection/lifecycle';
import { NoteDialog } from '../NoteDialog';
import { VARIANT_CONFIRMATION_DETAIL_PATH } from '../paths';
import type { VariantConfirmation } from '../types';
import { getNomenclatureNames } from '../utils';

import { NotesIcon } from './NotesIcon';

function stopRowClickPropagation(event: MouseEvent) {
  event.stopPropagation();
}

const dataGridTooltipSlotProps = { popper: { disablePortal: false } };

const truncatedCellOuterSx = {
  minWidth: 0,
  width: '100%',
  overflow: 'hidden',
};

const truncatedCellTextSx = {
  display: 'block',
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

function useTruncationObserver(ref: RefObject<HTMLElement | null>, measureKey: string) {
  const [isTruncated, setIsTruncated] = useState(false);

  // nosemgrep:semgrep-rules.ui-useEffect
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measureKey, ref]);

  return isTruncated;
}

export interface AccessionCellProps {
  confirmation: VariantConfirmation;
  // The list view's row click already opens the confirmation, so the accession
  // is click-to-copy text there. Elsewhere (dialogs) there is no surrounding
  // row navigation, so render it as an external link that opens the
  // confirmation in a new tab.
  asExternalLink?: boolean;
}

export function AccessionCell(props: AccessionCellProps) {
  const { confirmation, asExternalLink = false } = props;
  const showMetadata = confirmation.for_ny || Boolean(confirmation.internal_notes);
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        alignContent: 'center',
        gap: 0.5,
        flexWrap: 'wrap',
      }}
    >
      {asExternalLink ? (
        <ExternalLink
          to={generatePath(VARIANT_CONFIRMATION_DETAIL_PATH, { id: String(confirmation.id) })}
          onClick={stopRowClickPropagation}
        >
          {confirmation.accession_number}
        </ExternalLink>
      ) : (
        <CopyableText text={confirmation.accession_number} />
      )}
      {showMetadata && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {confirmation.for_ny && <Chip label="NY" size="small" />}
          {confirmation.internal_notes && (
            <NotesIcon notes={confirmation.internal_notes} data-testid="internal-notes-icon" />
          )}
        </Box>
      )}
    </Box>
  );
}

export interface GHgvsCellProps {
  confirmation: VariantConfirmation;
}

function GHgvsCellWithLink(props: { name: string; variantClassificationId: number }) {
  const { name, variantClassificationId } = props;
  const linkRef = useRef<HTMLAnchorElement>(null);
  const isTruncated = useTruncationObserver(linkRef, name);

  return (
    <Box sx={truncatedCellOuterSx}>
      <Tooltip
        title={name}
        disableHoverListener={!isTruncated}
        slotProps={dataGridTooltipSlotProps}
      >
        <ExternalLink
          ref={linkRef}
          to={generatePath(VARIANT_CLASSIFICATION_DETAIL_PATH, {
            id: variantClassificationId,
          })}
          onClick={stopRowClickPropagation}
          sx={truncatedCellTextSx}
        >
          {name}
        </ExternalLink>
      </Tooltip>
    </Box>
  );
}

export function GHgvsCell(props: GHgvsCellProps) {
  const { confirmation } = props;
  const name = getNomenclatureNames(confirmation.variant.nomenclatures, 'gHGVS', true);
  if (!name) {
    return null;
  }
  if (confirmation.variant_classification_id) {
    return (
      <GHgvsCellWithLink
        name={name}
        variantClassificationId={confirmation.variant_classification_id}
      />
    );
  }
  return <CopyableText text={name} />;
}

export interface IgvBarcodeLinkCellProps {
  igvLink: string;
  barcode: string;
}

export function IgvBarcodeLinkCell(props: IgvBarcodeLinkCellProps) {
  const { igvLink, barcode } = props;
  const linkRef = useRef<HTMLAnchorElement>(null);
  const isTruncated = useTruncationObserver(linkRef, barcode);

  return (
    <Box sx={truncatedCellOuterSx}>
      <Tooltip
        title={barcode}
        disableHoverListener={!isTruncated}
        slotProps={dataGridTooltipSlotProps}
      >
        <ExternalLink
          ref={linkRef}
          to={igvLink}
          onClick={stopRowClickPropagation}
          sx={truncatedCellTextSx}
        >
          {barcode}
        </ExternalLink>
      </Tooltip>
    </Box>
  );
}

export interface NotesCellProps {
  label: string;
  notes: string | null | undefined;
}

export function NotesCell(props: NotesCellProps) {
  const { label, notes } = props;
  const textRef = useRef<HTMLSpanElement>(null);
  const isTruncated = useTruncationObserver(textRef, notes ?? '');
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!notes) return null;

  const openDialog = (event: MouseEvent) => {
    stopRowClickPropagation(event);
    setDialogOpen(true);
  };

  return (
    <Box sx={truncatedCellOuterSx}>
      <Tooltip
        title={notes}
        disableHoverListener={!isTruncated}
        slotProps={dataGridTooltipSlotProps}
      >
        <Box
          component="span"
          ref={textRef}
          onClick={openDialog}
          sx={{
            ...truncatedCellTextSx,
            cursor: 'pointer',
            color: 'primary.main',
            textDecoration: 'underline',
          }}
        >
          {notes}
        </Box>
      </Tooltip>
      {dialogOpen && <NoteDialog title={label} note={notes} onClose={() => setDialogOpen(false)} />}
    </Box>
  );
}

// Primer actions that block the next lifecycle step (request / approve /
// complete) and must be surfaced in the list so reviewers can see at a glance
// which rows are waiting on them. `replace` is intentionally excluded — it is
// optional in `availablePrimerActions` and not a blocker.
const REQUIRED_PRIMER_ACTIONS = ['assign', 'review-suggestion', 'resolve-completion'] as const;
type RequiredPrimerAction = (typeof REQUIRED_PRIMER_ACTIONS)[number];

const PRIMER_ACTION_LABEL: Record<RequiredPrimerAction, string> = {
  assign: 'Assign primers',
  'review-suggestion': 'Review suggestion',
  'resolve-completion': 'Validate or fail',
};

const PRIMER_ACTION_CHIP_COLOR: Record<RequiredPrimerAction, 'warning' | 'info'> = {
  assign: 'warning',
  'review-suggestion': 'info',
  'resolve-completion': 'warning',
};

function getRequiredPrimerAction(confirmation: VariantConfirmation): RequiredPrimerAction | null {
  const actions = availablePrimerActions(confirmation);
  return REQUIRED_PRIMER_ACTIONS.find((action) => actions.has(action)) ?? null;
}

export function getPrimersCellValue(confirmation: VariantConfirmation): string {
  const requiredAction = getRequiredPrimerAction(confirmation);
  if (requiredAction !== null) return PRIMER_ACTION_LABEL[requiredAction];
  return confirmation.primer_set?.state ?? '';
}

export interface PrimersCellProps {
  confirmation: VariantConfirmation;
}

export function PrimersCell(props: PrimersCellProps) {
  const { confirmation } = props;
  const requiredAction = getRequiredPrimerAction(confirmation);
  if (requiredAction !== null) {
    return (
      <Chip
        label={PRIMER_ACTION_LABEL[requiredAction]}
        size="small"
        color={PRIMER_ACTION_CHIP_COLOR[requiredAction]}
      />
    );
  }
  const primerState = confirmation.primer_set?.state;
  if (!primerState) return null;
  return <Text variant="body2">{primerState}</Text>;
}
